/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { FlagOutlined, OpenInFull } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

import {
  getBoxAspectRatio,
  getCropImageStyle,
  getCropOverlayStyle,
  NaturalImageSize,
} from './classificationUtils'
import type { ClassificationBoundingBox } from './classificationTypes'

const CROP_VIEWPORT = {
  width: 200,
  height: 238,
}

type CropCardProps = {
  box: ClassificationBoundingBox
  rejected: boolean
  readOnly?: boolean
  onToggleRejected: () => void
  onOpenPreview: () => void
  onFlag?: () => void
  statusLabel?: string
  statusColor?: 'success' | 'error' | 'warning' | 'default'
}

const CropCard: React.FC<CropCardProps> = ({
  box,
  rejected,
  readOnly = false,
  onToggleRejected,
  onOpenPreview,
  onFlag,
  statusLabel,
  statusColor,
}) => {
  const { t } = useT()
  const [naturalSize, setNaturalSize] = React.useState<NaturalImageSize | null>(
    null,
  )
  const boxAspectRatio = React.useMemo(
    () => Math.min(Math.max(getBoxAspectRatio(box), 0.65), 2.2),
    [box],
  )
  const cropViewport = React.useMemo(
    () => ({
      width: CROP_VIEWPORT.width,
      height: CROP_VIEWPORT.width / boxAspectRatio,
    }),
    [boxAspectRatio],
  )

  const cropStyle = naturalSize
    ? getCropImageStyle(box, naturalSize, cropViewport)
    : undefined
  const cropOverlayStyle = naturalSize
    ? getCropOverlayStyle(box, naturalSize, cropViewport)
    : undefined
  const hiddenImageStyle = React.useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      pointerEvents: 'none',
    }),
    [],
  )

  return (
    <Card
      sx={theme => ({
        position: 'relative',
        border: '1px solid',
        borderColor: rejected
          ? theme.palette.error.main
          : theme.palette.divider,
        ...(box.isFlagged
          ? {
              borderColor: theme.palette.warning.main,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(245, 158, 11, 0.14)'
                  : 'rgba(255, 247, 237, 0.95)',
              opacity: 0.7,
            }
          : null),
        backgroundColor: rejected
          ? theme.palette.mode === 'dark'
            ? 'rgba(239, 68, 68, 0.12)'
            : 'rgba(254, 226, 226, 0.9)'
          : theme.palette.background.paper,
        opacity: box.isFlagged ? 0.7 : rejected ? 0.74 : 1,
        transition:
          'transform 160ms ease, border-color 160ms ease, opacity 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      })}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Stack spacing={1}>
          <Box
            sx={theme => ({
              position: 'relative',
              width: '100%',
              aspectRatio: `${cropViewport.width} / ${cropViewport.height}`,
              borderRadius: 2,
              overflow: 'hidden',
              cursor: box.isFlagged || readOnly ? 'default' : 'pointer',
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))'
                  : 'linear-gradient(180deg, #f8fafc, #e2e8f0)',
            })}
            onClick={box.isFlagged || readOnly ? undefined : onToggleRejected}
          >
            <img
              src={box.sourceImageUrl}
              alt=""
              onLoad={event => {
                setNaturalSize({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }}
              style={cropStyle ?? hiddenImageStyle}
            />
            {cropOverlayStyle && <Box sx={cropOverlayStyle} />}
            {!naturalSize && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <CircularProgress size={22} />
              </Box>
            )}
            <Tooltip
              title={t('cropCard.inspectFullImage', 'Inspect full image')}
            >
              <IconButton
                onClick={event => {
                  event.stopPropagation()
                  onOpenPreview()
                }}
                size="small"
                sx={theme => ({
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(15, 23, 42, 0.72)'
                      : 'rgba(255, 255, 255, 0.88)',
                })}
              >
                <OpenInFull fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Chip
              size="small"
              label={
                statusLabel ??
                (box.isFlagged
                  ? t('cropCard.status.flagged', 'Flagged')
                  : rejected
                    ? t('cropCard.status.rejected', 'Rejected')
                    : t('cropCard.status.accepted', 'Accepted'))
              }
              color={
                statusColor ??
                (box.isFlagged ? 'warning' : rejected ? 'error' : 'success')
              }
              variant={
                statusLabel
                  ? 'filled'
                  : box.isFlagged || rejected
                    ? 'filled'
                    : 'outlined'
              }
            />
            {!box.isFlagged && !readOnly && onFlag ? (
              <Tooltip title={t('cropCard.flagCrop', 'Flag crop')}>
                <IconButton
                  aria-label={t('cropCard.flagCrop', 'Flag crop')}
                  onClick={event => {
                    event.stopPropagation()
                    onFlag()
                  }}
                  size="small"
                >
                  <FlagOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default CropCard
